import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

function Sheet({ open, onOpenChange, children }: SheetProps) {
  React.useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50">{children}</div>,
    document.body,
  );
}

function SheetOverlay({ onClick }: { onClick?: () => void }) {
  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in-0"
      onClick={onClick}
    />
  );
}

function SheetContent({
  side = "right",
  className,
  children,
  onClose,
}: {
  side?: "left" | "right" | "bottom" | "top";
  className?: string;
  children: React.ReactNode;
  onClose?: () => void;
}) {
  const sideClass = {
    left: "inset-y-0 left-0 h-full w-3/4 border-r animate-in slide-in-from-left",
    right: "inset-y-0 right-0 h-full w-3/4 border-l animate-in slide-in-from-right",
    bottom: "inset-x-0 bottom-0 max-h-[85vh] w-full border-t rounded-t-lg animate-in slide-in-from-bottom",
    top: "inset-x-0 top-0 w-full border-b animate-in slide-in-from-top",
  }[side];

  return (
    <div
      className={cn(
        "fixed z-50 grid gap-4 bg-background shadow-lg",
        sideClass,
        className,
      )}
    >
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className={cn(
            "absolute top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            side === "left" || side === "top" ? "right-4" : "right-4",
          )}
        >
          <X className="h-5 w-5" />
          <span className="sr-only">关闭</span>
        </button>
      )}
      {children}
    </div>
  );
}

function SheetHeader({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn("flex flex-col space-y-2 p-6", className)}>{children}</div>;
}

function SheetTitle({ className, children }: { className?: string; children: React.ReactNode }) {
  return <h2 className={cn("text-lg font-semibold text-foreground", className)}>{children}</h2>;
}

function SheetBody({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn("px-6 pb-6", className)}>{children}</div>;
}

export { Sheet, SheetOverlay, SheetContent, SheetHeader, SheetTitle, SheetBody };
